<script setup>
import PrimaryButton from "../../../../frontend/src/components/ui/PrimaryButton.vue";
import {onMounted, reactive, ref, watch} from "vue";
import useForm from "@/composables/useForm";
import aiSequencesService from "@/services/ai-sequences.service";
import aiPromptsService from "@/services/ai-prompts.service";
import VueMultiselect from 'vue-multiselect'

const emit = defineEmits(['saved']);

const servicesNames = {
    gpt: 'GPT',
    gemini: 'Gemini',
    ppx: 'Perplexity',
    claude: 'Claude',
}

const form = reactive({
    name: null,
    description: null,
    steps: [],
})

const {populateForm, resetForm} = useForm(form);
const showModal = ref(false);
const id = ref(null);
const loading = ref(false);
const currentStep = ref(null);
const hasErrors = ref(false);
const promptsList = ref([]);

const steps = ref([]);

watch(steps, (value) => {
    form.steps = [
        ...value.map(step => ({
            id: step.id,
            prompt_id: step.prompt ? step.prompt.id : null,
            service: step.service,
            model: step.model,
            loading_text: step.loading_text,
        }))
    ];
}, {deep: true});

onMounted(() => {
    getPromptsList();
})

async function getData() {
    const {data} = await aiSequencesService.getEdit(id.value);
    populateForm(data);
    steps.value = [];

    for (let step of form.steps) {
        const prompt = promptsList.value.find(item => item.id === step.prompt_id);

        if (prompt) {
            step.prompt = prompt;
        }

        steps.value.push({...step});
    }

    if (form.steps.length) {
        currentStep.value = 0;
    }
}

async function getPromptsList() {
    const {data} = await aiPromptsService.getList();
    promptsList.value = data;
}

function addStep() {
    steps.value.push({
        id: null,
        prompt: null,
        service: null,
        model: null,
        loading_text: '',
    });

    currentStep.value = steps.value.length - 1;
}

function removeStep(index) {
    if (!confirm("Remove step?")) {
        return;
    }

    steps.value.splice(index, 1);

    if (currentStep.value >= steps.value.length) {
        currentStep.value = steps.value.length - 1;
    }
}

function validate() {
    hasErrors.value = false;

    for (let index = 0; index < form.steps.length; index++) {
        const step = form.steps[index];

        if (!step.prompt_id || !step.service || !step.loading_text) {
            hasErrors.value = true;
            currentStep.value = index;
            break;
        }
    }

    return !hasErrors.value;
}

async function save() {
    if (!validate()) {
        return;
    }

    loading.value = true;

    try {
        await aiSequencesService.save(id.value, form);
        showModal.value = false;
        emit('saved');
    } finally {
        loading.value = false;
    }
}

function open(itemId) {
    currentStep.value = null;
    resetForm();
    id.value = itemId;
    showModal.value = true;
    getData();
}

defineExpose({open});
</script>

<template>
    <b-modal
        v-model="showModal"
        modal-class="modal-w-md" :title="(id ? 'Edit' : 'Add') + ' Sequence ' + form.name"
    >
        <form class="form pb-5">
            <div class="form__item">
                <h5 class="heading5 heading5--secondary">Description</h5>
                <input class="form-control--mid form-control" type="text" v-model="form.description">
            </div>

            <ul v-if="steps.length" class="nav nav-tabs">
                <li v-for="(step, index) in steps" :key="step.name" class="nav-item">
                    <a class="nav-link" :class="{'active': currentStep === index}" @click="currentStep = index">Step {{index + 1}}</a>
                </li>

                <li class="nav-item">
                    <a class="nav-link" @click="addStep">+ Add</a>
                </li>
            </ul>

            <div v-for="(_, index) in steps" :key="index">
                <div v-if="currentStep === index" class="mt-2">
                    <div style="display: flex; justify-content: flex-end;">
                        <button type="button" class="btn btn-danger" @click="removeStep(index)">
                            Remove Step
                        </button>
                    </div>

                    <template v-if="hasErrors">
                        <div class="form__error">Prompt, Service and Loading Text must be filled</div>
                    </template>

                    <div class="form__item">
                        <h5 class="heading5 heading5--secondary">Prompt</h5>

                        <VueMultiselect
                            v-model="steps[index].prompt"
                            :options="promptsList"
                            label="name"
                            placeholder="Select a prompt"
                            class="mb-3"
                        />

                        <div v-if="steps[index].prompt" class="p-3 border rounded bg-light">
                            {{steps[index].prompt.text}}
                        </div>
                    </div>

                    <div class="form__item">
                        <h5 class="heading5 heading5--secondary">Service</h5>

                        <select class="form-control--mid form-control" v-model="steps[index].service">
                            <option
                                v-for="(name, id) in servicesNames"
                                :key="id"
                                :value="id"
                            >
                                {{ name }}
                            </option>
                        </select>
                    </div>

                    <div class="form__item">
                        <h5 class="heading5 heading5--secondary">Model</h5>

                        <select class="form-control--mid form-control" v-model="steps[index].model">
                            <option value="null">
                                Default
                            </option>
                        </select>
                    </div>

                    <div class="form__item">
                        <h5 class="heading5 heading5--secondary">Loading Text</h5>

                        <input
                            type="text"
                            v-model="steps[index].loading_text"
                            class="form-control--mid form-control"
                        />
                    </div>
                </div>
            </div>
        </form>

        <template #modal-footer>
            <PrimaryButton
                :loading="loading"
                @click="save"
            />
        </template>
    </b-modal>
</template>

<style scoped>

</style>
